import React from "react";
import { useTranslation } from "react-i18next";
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  BookOpen,
} from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";

// Instructor profile hero — ambient primary glow + avatar ring, matching the
// same visual language as the course-detail Instructor box and the site's
// other hero-style sections (Contact, NotFound).
const InstructorHero = ({ data }) => {
  const { t } = useTranslation();

  const getImageUrl = () => {
    const imagePath = data?.profileImage || data?.image;
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BASE_URL.replace(/\/api$/, "")}/${imagePath}`;
  };

  const imageUrl = getImageUrl();
  const courseCount = data?.courses?.length || 0;

  const socials = [
    data?.facebook && { href: data.facebook, Icon: FacebookLogo, label: "Facebook" },
    data?.instagram && { href: data.instagram, Icon: InstagramLogo, label: "Instagram" },
    data?.linkedin && { href: data.linkedin, Icon: LinkedinLogo, label: "LinkedIn" },
  ].filter(Boolean);

  return (
    <section className="relative isolate overflow-hidden py-12 sm:py-16">
      {/* Ambient brand glow — decorative */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 start-1/4 h-72 w-72 rounded-full bg-primary/20 blur-[110px]" />
        <div className="absolute bottom-0 end-0 h-56 w-56 rounded-full bg-darkPrimary/25 blur-[100px]" />
      </div>

      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-8">
        {/* Avatar */}
        <div className="shrink-0">
          <div className="w-32 h-32 sm:w-44 sm:h-44 rounded-full overflow-hidden ring-4 ring-primary/30 shadow-[0_0_40px_rgba(237,26,77,0.25)]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={data?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-lightGrey flex items-center justify-center text-white font-bold text-4xl">
                {data?.name?.charAt(0) || "I"}
              </div>
            )}
          </div>
        </div>

        {/* Name, bio, socials */}
        <div className="flex flex-col gap-3 min-w-0">
          <div>
            <p className="text-primary text-xs uppercase tracking-[0.14em] font-semibold">
              {t("courses.instructor") || "Instructor"}
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">
              {data?.name}
            </h1>
          </div>

          {courseCount > 0 && (
            <span className="inline-flex w-max items-center gap-1.5 text-xs font-medium text-white/80 bg-white/[0.06] border border-white/[0.08] px-3 py-1.5 rounded-full">
              <BookOpen size={14} className="text-primary" weight="fill" />
              {courseCount} {t("general.courses", "Courses")}
            </span>
          )}

          {data?.bio && (
            <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-2xl">
              {data.bio}
            </p>
          )}

          {socials.length > 0 && (
            <div className="flex items-center gap-2 mt-1">
              {socials.map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="
                    w-11 h-11 rounded-xl
                    bg-white/[0.06] border border-white/[0.08]
                    flex items-center justify-center
                    text-white/80 hover:text-primary hover:border-primary/40 hover:bg-white/[0.1]
                    transition-colors
                  "
                >
                  <Icon size={20} weight="bold" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InstructorHero;
